<template>
  <div>
    <router-view @updated="refetch" />
    <div
      v-if="appointmentReports.length"
      :class="$style.header"
    >
      <BaseButton
        :routerLink="{ name: 'customer-report-create' }"
        v-test="'customerCreateReport'"
      >
        {{ $t('global.actions.create_report') }}
      </BaseButton>
    </div>
    <BaseCard v-if="appointmentReports.length">
      <BaseTable
        :headers="
          [
            $t('global.date'),
            hasSingleEmployee ? '' : $t('global.items.employee', 1),
            $t('global.description'),
            $t('global.attachments')
          ].filter((header) => !!header)
        "
        :rows="
          appointmentReports.map((report) => ({
            id: report.id,
            routerLink: {
              name: 'customer-report',
              params: {
                id: report.id
              }
            },
            cells: [
              {
                value: filters.date(report.createdAt),
                type: 'date'
              },
              getEmployeeName(report),
              filters.stripHtml(report.report ?? ''),
              !!report.attachments.length
            ].filter((cell) => cell !== undefined),
            actions: ['edit', 'delete']
          }))
        "
        @action="onOptionClick"
        v-test="'customer-reports'"
      />
    </BaseCard>
    <LoadMore
      v-show="!allDataFetched"
      @shown="fetchMore"
    />
    <EmptyPageContent
      v-if="!loading && !appointmentReports.length"
      imageName="reports"
      :text="{
        title: $t('customers.empty_state.reports.title'),
        description: $t('customers.empty_state.reports.description'),
        btnPrimary: $t('global.actions.create_report')
      }"
      :moreLink="{
        text: $t('customers.empty_state.reports.more_text'),
        url: helpCenterArticleUrl($t('customers.empty_state.reports.more_url'))
      }"
      @click="$router.push({ name: 'customer-report-create' })"
    />
  </div>
</template>

<script
  setup
  lang="ts"
>
import { useMutation } from '@vue/apollo-composable';
import gql from 'graphql-tag';
import { computed, inject, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { usePagination } from '@/apollo/pagination';
import EmptyPageContent from '@/components/EmptyPageContent.vue';
import LoadMore from '@/components/LoadMore.vue';
import filters from '@/filters';
import { helpCenterArticleUrl } from '@/helpers/help-center';
import { flash, modal } from '@/helpers/ui';
import { useResourcesStore } from '@/stores/resources';
import type { Customer } from '@/types';

const customer = inject<Ref<Customer | undefined>>('customer');
const customerId = computed(() => customer?.value?.id);

const { hasSingleEmployee, resourceById } = useResourcesStore();

const { appointmentReports, allDataFetched, loading, fetchMore, refetch } =
  usePagination({
    fieldName: 'appointmentReports',
    query: gql`
      query getAppointmentReports(
        $pagination: PaginationAttributes!
        $customerId: Int!
      ) {
        appointmentReports(pagination: $pagination, customerId: $customerId) {
          appointment {
            employeeNames
          }
          attachments {
            id
          }
          createdAt
          id
          employeeId
          report
        }
      }
    `,
    variables: {
      customerId: customerId.value,
    },
    options: {
      enabled: computed(() => !!customerId.value),
    },
  });

const getEmployeeName = (report: any) => {
  if (hasSingleEmployee) {
    return undefined;
  } else if (report.appointment) {
    return report.appointment.employeeNames;
  } else {
    return resourceById(report.employeeId)?.name || '';
  }
};

const router = useRouter();
const { t } = useI18n();

const onOptionClick = ({ id, option }: { id: number; option: string }) => {
  if (option === 'edit') {
    router.push({ name: 'customer-report-edit', params: { id } });
  } else if (option === 'delete') {
    modal('confirmation', {
      type: 'delete',
      item: t('global.items.report'),
    }).then(() => {
      const { mutate } = useMutation(gql`
        mutation deleteAppointmentReport(
          $input: DeleteAppointmentReportInput!
        ) {
          deleteAppointmentReport(input: $input) {
            appointmentReport {
              id
            }
          }
        }
      `);

      mutate({
        input: {
          id,
        },
      }).then(() => {
        flash(t('global.flash.report_deleted'));
        refetch();
      });
    });
  }
};
</script>

<style
  lang="scss"
  module
>
.header {
  display: flex;
  justify-content: flex-end;
  margin-bottom: $spacing;
}
</style>
